import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import {
  auditCsvUrl,
  useInfiniteAuditLog,
  type AuditFilters,
  type AuditRow,
} from '@/hooks/data/useAuditLog';
import { useAgents } from '@/hooks/data/useAgents';
import { fieldLabel, valueLabel } from '@/lib/historyLabels';
import { fetchMe } from '@/lib/auth';
import { useInfiniteScrollSentinel } from '@/hooks/useInfiniteScrollSentinel';
import { formatDateTime } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/admin/audit-log')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (!me || me.profile.role !== 'admin') throw redirect({ to: '/' });
  },
  component: AuditLogPage,
});

const RESOURCE_LABELS: Record<AuditRow['resource_type'], string> = {
  client: 'Klient',
  property: 'Immobilie',
  appointment: 'Termin',
  offer: 'Anbot',
};

function AuditLogPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<Omit<AuditFilters, 'limit'>>({});
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteAuditLog(filters);
  const rows = useMemo<AuditRow[]>(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );
  const sentinelRef = useInfiniteScrollSentinel<HTMLTableRowElement>(
    () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    !!hasNextPage,
  );
  // Batch ZV — agents list to populate the user filter dropdown +
  // resolve user_id → human-readable name in the Benutzer column.
  const { data: agents = [] } = useAgents();
  const userMap = useMemo(
    () => new Map(agents.map((a) => [a.user_id, a])),
    [agents],
  );

  const setF = <K extends keyof AuditFilters>(k: K, v: AuditFilters[K]) =>
    setFilters((p) => ({ ...p, [k]: v }));

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {t('audit.title', 'Audit Log')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('audit.subtitle', 'Alle CRUD-Änderungen an Klienten, Immobilien, Terminen, Anboten.')}
          </p>
        </div>
        <a
          href={auditCsvUrl(filters)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-background/60 px-4 text-sm hover:border-primary/40"
        >
          {t('audit.export', 'CSV exportieren')}
        </a>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t('audit.filter.table', 'Tabelle')}</label>
          <Select
            value={filters.resourceType ?? ''}
            onChange={(e) =>
              setF(
                'resourceType',
                (e.target.value || undefined) as AuditFilters['resourceType'],
              )
            }
            className="max-w-xs"
          >
            <option value="">{t('common.all', 'Alle')}</option>
            <option value="client">{t('nav.clients', 'Klienten')}</option>
            <option value="property">{t('nav.properties', 'Immobilien')}</option>
            <option value="appointment">{t('nav.appointments', 'Termine')}</option>
            <option value="offer">{t('nav.offers', 'Kaufanbote')}</option>
          </Select>
        </div>
        {/* Batch ZV+ZW — user filter via searchable picker. Admin can
            scroll OR type to find a user; matches against full_name +
            agent_id + role with diacritic-insensitive comparison. */}
        <div className="space-y-1 min-w-[260px]">
          <label className="text-xs text-muted-foreground">
            {t('audit.filter.user', 'Benutzer')}
          </label>
          <SearchablePicker
            value={filters.userId ?? ''}
            onChange={(v) => setF('userId', v || undefined)}
            placeholder={t('audit.filter.user_placeholder', 'Alle Benutzer')}
            emptyText={t('audit.filter.user_empty', 'Keine Treffer')}
            options={agents
              .slice()
              .sort((a, b) => a.full_name.localeCompare(b.full_name, 'de'))
              .map((a) => ({
                value: a.user_id,
                label: a.full_name,
                secondary:
                  (a.agent_id ? `${a.agent_id} · ` : '') +
                  (a.role === 'admin'
                    ? 'Admin'
                    : a.role === 'call_center'
                      ? 'Call Center'
                      : 'Makler'),
              }))}
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="text-xs text-muted-foreground">Suche</label>
          <Input
            value={filters.search ?? ''}
            onChange={(e) => setF('search', e.target.value || undefined)}
            placeholder="Feld, alter Wert, neuer Wert, Grund …"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Von</label>
          <Input
            type="date"
            onChange={(e) =>
              setF('from', e.target.value ? new Date(e.target.value).getTime() : undefined)
            }
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Bis</label>
          <Input
            type="date"
            onChange={(e) =>
              setF('to', e.target.value ? new Date(e.target.value).getTime() : undefined)
            }
          />
        </div>
        <Button variant="outline" onClick={() => setFilters({})}>
          {t('common.reset', 'Zurücksetzen')}
        </Button>
      </div>

      <div className="fm-table-wrap rounded-2xl border border-border bg-card shadow-soft">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/30 text-start text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Zeit</th>
              <th className="px-4 py-3">{t('audit.col.user', 'Benutzer')}</th>
              <th className="px-4 py-3">Typ</th>
              <th className="px-4 py-3">Aktion</th>
              <th className="px-4 py-3">Feld</th>
              <th className="px-4 py-3">Alt → Neu</th>
              <th className="px-4 py-3">Auto</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  Lädt …
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                  Keine Einträge.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const u = r.user_id ? userMap.get(r.user_id) : null;
              const isAuto = !!r.automatic_change;
              return (
              <tr key={r.id} className="border-t border-border/60 align-top">
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                  {formatDateTime(new Date(r.created_at), {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </td>
                {/* Batch ZV — Benutzer column. Falls back to "System" for
                    automatic_change rows or "—" when the actor can't be
                    resolved (e.g. user soft-deleted since the action). */}
                <td className="px-4 py-3">
                  {u ? (
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar
                        src={u.avatar_url ?? null}
                        name={u.full_name}
                        size="sm"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{u.full_name}</p>
                        <p className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
                          {u.role === 'admin'
                            ? 'Admin'
                            : u.role === 'makler'
                              ? 'Makler'
                              : 'Call Center'}
                        </p>
                      </div>
                    </div>
                  ) : isAuto ? (
                    <span className="text-xs text-muted-foreground italic">
                      {t('audit.user.system', 'System')}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline">{t(`audit.resource.${r.resource_type}`, RESOURCE_LABELS[r.resource_type])}</Badge>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{r.action}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {fieldLabel(r.resource_type, r.field_changed)}
                </td>
                <td className="px-4 py-3 text-xs">
                  {r.old_value || r.new_value ? (
                    <div className="space-y-0.5">
                      {r.old_value && (
                        <div className="text-destructive line-through">
                          {valueLabel(r.resource_type, r.field_changed, r.old_value, t)}
                        </div>
                      )}
                      {r.new_value && (
                        <div className="text-success">
                          {valueLabel(r.resource_type, r.field_changed, r.new_value, t)}
                        </div>
                      )}
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-3">
                  {r.automatic_change ? <Badge variant="secondary">auto</Badge> : null}
                </td>
              </tr>
              );
            })}
            {/* Sentinel — IntersectionObserver triggers fetchNextPage. */}
            {hasNextPage && (
              <tr ref={sentinelRef}>
                <td
                  colSpan={7}
                  className="px-4 py-6 text-center text-xs text-muted-foreground"
                >
                  {isFetchingNextPage
                    ? t('common.loading_more', 'Lädt weitere …')
                    : ''}
                </td>
              </tr>
            )}
            {!hasNextPage && rows.length > 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-4 text-center text-[11px] uppercase tracking-wide text-muted-foreground/60"
                >
                  {t('common.end_of_list', 'Ende der Liste')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
